import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';

import { AzavistaApiService, IJobFileDownloadData } from '@azavista/servicelib';
import { prefixWithUrlForResources } from './shared.service';
import { getFormattedFileNameWithExtension } from './file.mime.utils';

type OnProgressFn = (params: {
    fileName: string;
    /** Bytes loaded */
    loaded: number;
    /** Total bytes */
    total: number;
    /** percentage of `loaded / total` */
    percentage: number;
}) => void;

@Injectable({
    providedIn: 'root'
})
export class FileDownloadService {

    constructor(private apiSvc: AzavistaApiService) {
    }

    async saveFileToDirectory(fileInfo: IJobFileDownloadData, directoryHandle: FileSystemDirectoryHandle, onProgressFn: OnProgressFn): Promise<void> {
        // make sure we download using correct filename
        fileInfo = {
            ...fileInfo,
            name: getFormattedFileNameWithExtension(fileInfo),
        };
        // For each file in the job response
        // Create file in selected directory
        const fileHandle = await directoryHandle.getFileHandle(fileInfo.name, {
            create: true
        });

        // Create writable stream
        const writable = await fileHandle.createWritable();

        // Fetch file from server
        const response = await fetch(prefixWithUrlForResources(fileInfo.url));

        // Track download progress
        const reader = response.body.getReader();
        const contentLength = fileInfo.size;
        let receivedLength = 0;

        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                onProgressFn({
                    fileName: fileInfo.name,
                    loaded: contentLength,
                    total: contentLength,
                    percentage: 100
                });
                break;
            };

            await writable.write(value);
            receivedLength += value.length;

            // Report progress
            if (onProgressFn) {
                onProgressFn({
                    fileName: fileInfo.name,
                    loaded: receivedLength,
                    total: contentLength,
                    percentage: Math.round((receivedLength / contentLength) * 100)
                });
            }
        }

        await writable.close();

    }

    async saveFile(fileUrl: string, fileName: string): Promise<void> {
        // TODO: Not all files are job files. In addition
        //       apiSvc.downloadJobFile internally prefixes the url with the base url https://.../3.0/
        //       and becomes unsuitable for downloading assets, which do not have /3.0/ in their URL
        const blob = await this.apiSvc.downloadJobFile(fileUrl);
        saveAs(blob, fileName);
    }

    async downloadAndSave(url: string, fileName: string): Promise<void> {
        const blob = await this.apiSvc.getFile(url);
        saveAs(blob, fileName);
    }
}
